import { useRef } from 'react'
import { Link } from 'react-router-dom'
import type { StudentWithSummary } from '../../types/studentSummary'
import { scrollToElementTop } from '../../utils/scrollToElement'

interface StudentListTableProps {
  students: StudentWithSummary[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  schoolId?: string
  filterGrade?: string | null
  filterClass?: string | null
  filterGroupId?: string | null
}

const StudentListTable = ({
  students,
  currentPage,
  totalPages,
  onPageChange,
  schoolId,
  filterGrade,
  filterClass,
  filterGroupId
}: StudentListTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // ページ切り替え後はテーブル上部まで戻す（下部のボタンから押したときに位置が迷子にならないように）
  const changePage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return
    onPageChange(page)
    requestAnimationFrame(() => scrollToElementTop(containerRef.current))
  }

  const renderPagination = (position: 'top' | 'bottom') => (
    <div
      className={`bg-gray-50 px-4 py-3 ${position === 'top' ? 'border-b' : 'border-t'} border-gray-200`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          ページ {currentPage} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage <= 1}
            className={`px-3 py-1 text-sm rounded transition-colors duration-200 ${
              currentPage <= 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            ← 前へ
          </button>
          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className={`px-3 py-1 text-sm rounded transition-colors duration-200 ${
              currentPage >= totalPages
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            次へ →
          </button>
        </div>
      </div>
    </div>
  )

  if (students.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <div className="text-gray-400 text-lg mb-4">👥</div>
        <p className="text-gray-600">表示できる生徒がいません</p>
        <p className="text-sm text-gray-500 mt-2">フィルターを変更してください</p>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="bg-white rounded-lg shadow overflow-hidden">
      {/* Pagination (top) */}
      {totalPages > 1 && renderPagination('top')}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12 whitespace-nowrap">
                学年
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12 whitespace-nowrap">
                組
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12 whitespace-nowrap">
                番号
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                名前
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                状況
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                更新日
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                動画数
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                ワーク数
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                要約
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {students.map((student) => (
              <tr key={student.id} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                  {student.grade || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                  {student.class || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                  {student.attendance_number || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <Link
                    to={(() => {
                      const params = new URLSearchParams()
                      if (schoolId) params.set('schoolId', schoolId)
                      if (filterGrade) params.set('grade', filterGrade)
                      if (filterClass) params.set('class', filterClass)
                      if (filterGroupId) params.set('groupId', filterGroupId)
                      const queryString = params.toString()
                      return `/teacher/students/${student.id}${queryString ? `?${queryString}` : ''}`
                    })()}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {student.name}
                  </Link>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center">
                  <span className="text-2xl" style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}>
                    {student.latestSummary?.weather_icon || '－'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-700">
                  {student.latestSummary?.summary_date
                    ? new Date(student.latestSummary.summary_date).toLocaleDateString('ja-JP', {
                        month: 'numeric',
                        day: 'numeric'
                      })
                    : '－'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-700">
                  {student.videoCount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-700">
                  {student.chatCount}
                </td>
                <td className="px-4 py-4 text-sm text-gray-700">
                  <div>
                    {student.latestSummary?.student_state || 'まだデータがありません'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (bottom) */}
      {totalPages > 1 && renderPagination('bottom')}
    </div>
  )
}

export default StudentListTable
